import { Moon, Search, Settings, Sun } from "lucide-react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { IntentButton } from "@/components/intent-button";
import { useTheme } from "@/hooks/use-theme";
import { jobNotifications, jobNotificationKeys } from "@/lib/job-notifications";
import { NotificationBell } from "@/components/notification-bell";

function WiredNotificationBell() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: jobNotificationKeys.unread,
    queryFn: () => jobNotifications.unread(),
  });
  const markRead = useMutation({
    mutationFn: (id: number) => jobNotifications.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: jobNotificationKeys.unread }),
  });
  return <NotificationBell notifications={data} onMarkRead={(id) => markRead.mutate(id)} />;
}

export function TopBar() {
  const { mode, setMode } = useTheme();
  return (
    <header className="flex h-14 items-center gap-4 border-b bg-background px-4" role="banner">
      <Link to="/" className="flex items-center gap-2 font-semibold">
        <img src="/allmark-logo.png" alt="Allmark" className="h-8 w-auto" />
        <span className="hidden md:inline">Quote Manager</span>
      </Link>
      <nav aria-label="Breadcrumb" className="ml-2 text-sm text-muted-foreground" />
      <IntentButton intent="ghost" className="ml-auto" aria-label="Open command palette">
        <Search className="size-4" />
        <span className="hidden md:inline">Search</span>
        <kbd className="hidden md:inline rounded bg-muted px-1.5 py-0.5 text-xs">Ctrl+K</kbd>
      </IntentButton>
      <WiredNotificationBell />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IntentButton intent="ghost" aria-label={`Theme: ${mode}. Change theme`}>
            <Sun className="size-4 dark:hidden" />
            <Moon className="hidden size-4 dark:block" />
          </IntentButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => setMode("light")}>Light</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setMode("dark")}>Dark</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setMode("system")}>System</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button aria-label="User menu" className="flex items-center">
            <Avatar><AvatarFallback>AA</AvatarFallback></Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem><Settings className="mr-2 size-4" />Settings</DropdownMenuItem>
          <DropdownMenuItem>Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
